import {Component, HostListener, Inject, Input, OnDestroy, OnInit, PLATFORM_ID,} from '@angular/core';
import {ActivatedRoute, Params, Router} from "@angular/router";
import {EVENT_DETAILS} from "src/app/app-routing.module";
import {environment} from "@environment";
import {DOCUMENT, isPlatformBrowser} from "@angular/common";
import {AbstractComponent, FilterItem} from "@components";
import {
  EventFilter,
  EventItem,
  EventItemType,
  FindEventsRequest,
  LengthRange,
  ListSortOrder,
  Lookups,
  Message,
  Paging,
  Sport,
  Waypoint
} from "@model";
import {LoaderService, PublicEventsService} from "@services";
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {getDateFromNgbDate, normalizedDate} from "@utility";
import {TranslocoService} from "@ngneat/transloco";
import {parseIdListParam} from "@resolvers";

@Component({
  selector: 'app-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss']
})
export class EventCalendarComponent extends AbstractComponent implements OnInit, OnDestroy, Paging<EventItem> {
  static DEFAULT_PAGE_SIZE: number = 51;

  @Input() countries: Lookups[] = [];
  @Input() sports: Sport[] = [];
  @Input() sportsForSearch: Sport[] = [];
  @Input() countriesForSearch: Lookups[] = [];
  @Input() listOfEvents: EventItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() favoriteItems: EventItem[] = [];
  @Input() unreadMessages?: Message[];

  calendarType: EventItemType = EventItemType.UPCOMING;
  name: string = "";

  dateTo?: NgbDate;
  dateFrom?: NgbDate;
  initialDate: NgbDate;

  lengthRanges: LengthRange[] = EventFilter.LENGTH_RANGES;
  page: number = 1;
  hasMoreEvents: boolean = false;
  images: string[] = [1, 2, 3].map(n => `${environment.assetsImagesPath}/assets/img/carousel/carousel${n}.png`);
  environment: any = environment;
  showFabButton: boolean = false;
  queryParamsFirstChange: boolean = true;
  isLoading: boolean = false;
  eventMapVisible: boolean = false;
  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  waypointList: Waypoint[] = [];
  isBrowser: boolean = false;
  withBackground: boolean = false;

  showClearAll : boolean = false;
  chosenMonthRange?: { from?: NgbDate, to?: NgbDate };

  countriesItems: FilterItem[] = [];
  sportsItems: FilterItem[] = [];
  lengthItems: FilterItem[] = [];

  filterGroups: { items: FilterItem[]; label: string }[] = [];


  constructor(private router: Router, private publicEventsService: PublicEventsService, private route: ActivatedRoute,
              @Inject(DOCUMENT) private document: Document, private loaderService: LoaderService, @Inject(PLATFORM_ID) private platformId: Object,
              private translocoService: TranslocoService) {
    super();
    const now = new Date();
    this.initialDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset !== 0;
  }

  ngOnInit(): void {
    this.subscribeToRouteDataChange();
    this.handleExploreResult(this.listOfEvents);
    this.initFilterValues();
  }


  initFilterValues() {
    this.initLengthFilter();
    this.initSportFilter();
    this.initCountryFilter();

    this.filterGroups = [
      { items: this.countriesItems, label: this.translocoService.translate('app.common.country') },
      { items: this.sportsItems, label: this.translocoService.translate('app.common.sport') },
      { items: this.lengthItems, label: this.translocoService.translate('explore.races.length') }
    ];
  }

  initSportFilter() {
    this.sportsForSearch.forEach(sport => {
      if (sport.id) {
        this.sportsItems.push(new FilterItem(sport.id!,
            this.translocoService.translate('app.common.sports.' + sport.id),
            'app.common.sports.' + sport.id, false))
      }
    });
  }

  initLengthFilter(){
    this.lengthRanges.forEach((length: LengthRange, index: number) => {
      const label : string = `${
          length.to && length.from
              ? length.from + " - " + length.to
              : length.to && !length.from
                  ? this.translocoService.translate("explore.races.length.to") + " " + length.to
                  : !length.to && length.from
                      ? this.translocoService.translate("explore.races.length.over") + " " + length.from
                      : ""
      } km`
      this.lengthItems.push(new FilterItem(index, label, "", false));
    })
  }

  initCountryFilter() {
    this.countriesForSearch.forEach(country => {
      if (country.id) {
        this.countriesItems.push(new FilterItem(country.id!, "", 'app.common.country.' + country.id, false))
      }
    });
  }

  findWayPointList(): void {
    this.waypointList = this.listOfEvents
    .filter(event => event.latitude && event.longitude)
    .map(event => ({
      latitude: event.latitude,
      longitude: event.longitude,
      city: event.city,
      country: event.country,
      pointNameEn: event.nameEng,
      pointNameHr: event.name,
      pointNameIt: event.nameIta,
      pointNameSlo: event.nameSlo,
      eventItem: event
    }));
  }

  private subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.url.subscribe(urlSegments => {
      const currentUrl = urlSegments.map(segment => segment.path).join('/');
      this.calendarType = currentUrl.includes("finishedEvents") ? EventItemType.FINISHED : EventItemType.UPCOMING;
    }));

    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay), distinctUntilChanged())
    .subscribe(queryParams => this.onRouteDataChange(queryParams)));
  }

  private onRouteDataChange(queryParams: Params) {
    this.name = queryParams['name'] || "";
    this.dateFrom = queryParams['dateFrom'] ? this.getNgbDateFromString(queryParams['dateFrom']) : undefined;
    this.dateTo = queryParams['dateTo'] ? this.getNgbDateFromString(queryParams['dateTo']) : undefined;
    if (!this.dateTo && EventItemType.FINISHED === this.calendarType) {
      this.dateTo = this.initialDate
    }

    if (!this.dateFrom && EventItemType.UPCOMING === this.calendarType) {
      this.dateFrom = this.initialDate
    }

    if (!(
        (this.calendarType === EventItemType.FINISHED && this.isSameNgbDate(this.dateTo, this.initialDate) && !this.dateFrom) ||
        (this.calendarType === EventItemType.UPCOMING && this.isSameNgbDate(this.dateFrom, this.initialDate) && !this.dateTo)
    )) {
      this.chosenMonthRange = { from: this.dateFrom, to: this.dateTo };
    }


    const sportIds: number[] | undefined = this.getSportValue(queryParams);
    const countriesIds: number[] | undefined = this.getCountryValue(queryParams);

    this.sportsItems.forEach(item => {
      item.selected = sportIds?.includes(item.id!);
    });
    this.countriesItems.forEach(item => {
      item.selected = countriesIds?.includes(item.id!);
    });


    const from = this.getLengthValue(queryParams, true);
    const to = this.getLengthValue(queryParams, false);
    const selectedRangeIndex = this.lengthRanges.findIndex(
        range => range.from === from && range.to === to
    );

    if (selectedRangeIndex !== -1) {
      this.lengthItems.filter(i => i.id === selectedRangeIndex).forEach(i => i.selected = true);
    }

    this.showClearAll = this.isFilterActive()

    this.eventMapVisible = this.getEventMapVisible(queryParams);
    if (!this.queryParamsFirstChange && this.isDateValid(queryParams['dateFrom']) && this.isDateValid(queryParams['dateTo']) && this.isLengthValid(queryParams['lengthTo']) && this.isLengthValid(queryParams['lengthFrom']) && this.isSportValid(queryParams['sport'])) {
      this.onExplore(false);
    }
    this.queryParamsFirstChange = false;
  }

  private changeQueryParams(): void {

    const queryParams: { [key: string]: any } = {};
    if (this.dateFrom) queryParams['dateFrom'] = normalizedDate(getDateFromNgbDate(this.dateFrom));
    if (this.dateTo) queryParams['dateTo'] = normalizedDate(getDateFromNgbDate(this.dateTo));

    const selectedSports = this.sportsItems.filter(i => i.selected).map(i=>i.id)
    if (selectedSports.length) queryParams['sports'] = selectedSports.join(',');

    const selectedCountries = this.countriesItems.filter(i => i.selected).map(i=>i.id)
    if (selectedCountries.length) queryParams['countries'] = selectedCountries.join(",");

    const selectedRange = this.getSelectedRange();

    if (selectedRange?.from) {
        queryParams['lengthFrom'] = selectedRange.from;
      }
    if (selectedRange?.to) {
        queryParams['lengthTo'] = selectedRange.to;
    }

    this.showClearAll = this.isFilterActive();

    if (this.name) queryParams['name'] = this.name;
    queryParams['eventMapVisible'] = this.eventMapVisible;
    this.router.navigate([], {relativeTo: this.route, queryParams, replaceUrl: true});
  }

  createFilter(): FindEventsRequest {
    return FindEventsRequest.createEventPageFilter(
      this.page,
      {
        name: this.name || undefined,
        lengthFrom: this.getSelectedRange()?.from,
        lengthTo: this.getSelectedRange()?.to,
        sports: this.sportsItems.filter(i => i.selected).map(i => i.id!),
        countries: this.countriesItems.filter(i => i.selected).map(i => i.id!),
        ...this.getEventFilterDates()
      },
      this.calendarType === EventItemType.FINISHED ? ListSortOrder.ASC : ListSortOrder.DESC
    );
  }

  getSelectedRange() : LengthRange | undefined{
    const selectedId = this.lengthItems.filter(i => i.selected).map(i => i.id)?.[0];

    if (selectedId !== undefined && selectedId >= 0 && selectedId < this.lengthRanges.length) {
      return this.lengthRanges[selectedId];
    }
    return undefined;
  }

  getEventFilterDates(): {
    dateTo: Date | undefined,
    dateFrom: Date | undefined,
  } {
    if (EventItemType.FINISHED === this.calendarType) {
      return {
        dateFrom: this.dateFrom ? getDateFromNgbDate(this.dateFrom) : undefined,
        dateTo: getDateFromNgbDate(this.dateTo ?? this.initialDate),
      }
    } else {
      return {
        dateTo: this.dateTo ? getDateFromNgbDate(this.dateTo) : undefined,
        dateFrom: getDateFromNgbDate(this.dateFrom ?? this.initialDate),
      }
    }
  }

  handleExploreResult(listOfEvents: EventItem[], loadMore?: boolean): void {
    if (listOfEvents && this.calendarType !== EventItemType.UPCOMING) {
      this.hasMoreEvents = listOfEvents.length > EventCalendarComponent.DEFAULT_PAGE_SIZE;
      listOfEvents = listOfEvents.slice(0, EventCalendarComponent.DEFAULT_PAGE_SIZE);
      if (loadMore && this.listOfEvents) {
        listOfEvents = this.listOfEvents.concat(listOfEvents);
      }
    }
    this.setListOfItems(listOfEvents);
  }

  setListOfItems(listOfEvents: EventItem[]): void {
    this.listOfEvents = listOfEvents.length > 0 ? listOfEvents : [];
    if (this.calendarType === EventItemType.UPCOMING) {
      this.findWayPointList();
    }
    this.page++;
  }

  onExplore(loadMore?: boolean) {
    if (!loadMore) this.page = 1;
    this.isLoading = !loadMore;
    this.loaderService.startLoading();
    const serviceMethod = this.calendarType === EventItemType.UPCOMING ? 'findEventCalendarList' : 'filterFinishedEventsList';
    this.publicEventsService[serviceMethod](this.createFilter()).subscribe(listOfEvents => {
      this.loaderService.dismissLoading();
      this.isLoading = false;
      this.handleExploreResult(listOfEvents, loadMore);
    }, (_) => {
      this.loaderService.dismissLoading();
      this.isLoading = false;
    });
  }

  loadMore(): void {
    this.onExplore(true);
  }

  isDateValid(dateValue?: string) {
    if (dateValue) {
      const [year, month, day] = dateValue.split('-');
      const normalizedMonth = month.length === 1 ? `0${month}` : month;
      const normalizedDay = day.length === 1 ? `0${day}` : day;
      dateValue = `${year}-${normalizedMonth}-${normalizedDay}`;
      return !isNaN(new Date(dateValue).getTime());
    } else {
      return true;
    }
  }

  isSportValid(sportValue?: number) {
    return !sportValue || this.sportsForSearch.some(sport => sport.id === sportValue);
  }

  isCountryValid(countryValue?: number) {
    return !countryValue || this.countriesForSearch.some(country => country.id === countryValue);
  }

  isLengthValid(lengthValue?: string | null): boolean {
    if (lengthValue === undefined || lengthValue === null || lengthValue === '') return true;

    const validValues = new Set<number>();
    EventFilter.LENGTH_RANGES.forEach(range => {
      if (range.from !== undefined) validValues.add(range.from);
      if (range.to !== undefined) validValues.add(range.to);
    });

    const parsedValue = Number(lengthValue);
    return !isNaN(parsedValue) && validValues.has(parsedValue);
  }

  getLengthValue(queryParams: any, isFrom: boolean): number | undefined {
    const rawValue = isFrom ? queryParams.lengthFrom : queryParams.lengthTo;
    if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;
    return this.isLengthValid(rawValue) ? Number(rawValue) : undefined;
  }

  getSportValue(queryParams: any): number[] | undefined {
    const { ids: ids, valid: paramValid } = parseIdListParam(queryParams, 'sports');
    const valid = ids.every((v: number) => this.isSportValid(v) && v > 0);
    return valid ? ids : undefined;
  }

  getCountryValue(queryParams: any): number[] | undefined {
    const { ids: ids, valid: paramValid } = parseIdListParam(queryParams, 'countries');
    const valid = ids.every((v: number) => this.isCountryValid(v) && v > 0);
    return valid ? ids : undefined;
  }

  getEventMapVisible(queryParams: any) {
    return queryParams.eventMapVisible === "true";
  }

  getNgbDateFromString(dateString?: string) {
    if (dateString) {
      const [year, month, day] = dateString.split('-').map(Number);
      if (!year || !month || !day) return undefined;
      return new NgbDate(year, month, day);
    }
    return undefined;
  }

  dateChanged(value?: { from?: NgbDate, to?: NgbDate }) {
    this.dateFrom = value?.from;
    this.dateTo = value?.to;
    this.chosenMonthRange = value;
    this.showClearAll = !!value;
    this.changeQueryParams();
  }


  onFilterChange(items: FilterItem[], filterKey: 'activity' | 'distance' | 'location') {
    const selectedIds = new Set(items.map(item => item.id!));
    const groupMap = {
      activity: this.sportsItems,
      distance: this.lengthItems,
      location: this.countriesItems
    } as const;

    groupMap[filterKey].forEach(item => item.selected = selectedIds.has(item.id!));

    this.showClearAll = this.isFilterActive();
    this.changeQueryParams();
  }


  handleInput(value: string) {
    this.name = value.trim();
    this.changeQueryParams();
  }

  goToEventDetailsPage(item: EventItem, isFinishedEvent: boolean): void {
    this.router.navigate([EVENT_DETAILS], {
      relativeTo: this.route,
      queryParams: {eventId: item.id, isFinishedEvent: isFinishedEvent}
    });
  }

  trackByFn(index: number, item: EventItem) {
    return item.id;
  }

  scrollToTop(): void {
    this.document.body.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'start'});
  }

  toggleEventMapVisible(): void {
    this.eventMapVisible = !this.eventMapVisible;
    this.changeQueryParams();
  }


  removeFilter(items: FilterItem[], item: FilterItem) {
    const target = items.find(i => i.id === item.id);
    if (target) {
      target.selected = false;
    }

    this.showClearAll = this.isFilterActive();
    this.changeQueryParams();
  }

  clearAllFilters() {
    this.dateFrom = undefined;
    this.dateTo = undefined;
    this.chosenMonthRange = undefined;
    [...this.sportsItems, ...this.lengthItems, ...this.countriesItems].forEach(f => f.selected = false)
    this.showClearAll = false;
    this.changeQueryParams();
  }


  isSameNgbDate(d1?: NgbDate, d2?: NgbDate): boolean {
    return !!d1 && !!d2 && d1.year === d2.year && d1.month === d2.month && d1.day === d2.day;
  }

  isFilterActive(): boolean {
  return [
    ...this.sportsItems,
    ...this.lengthItems,
    ...this.countriesItems
  ].some(item => item.selected) || !!this.chosenMonthRange;
}

  protected readonly EventItemType = EventItemType;

}
